import { createFeatureSelector, createSelector } from '@ngrx/store';
import { some } from 'lodash-es';

import { cpFeatureKey, cpNodeAdapter, cpNodeCustomSelectors, CpState } from './cp.state';
import { cpNodeMatchesFilters } from '../cp-node';
import { isNotUndefined } from '../cp-configuration/utils/utils';

const cpNodeSelectors = cpNodeAdapter.getSelectors();

/**
 * Feature
 */
export const selectCp = createFeatureSelector<CpState>(cpFeatureKey);

/**
 * Stato
 */
export const selectCpDialog = createSelector(selectCp, (state) => state.dialog);

export const selectCpDialogVisible = createSelector(selectCpDialog, (dialog) => dialog.visible);

export const selectCpLayoutType = createSelector(selectCp, (state) => state.layoutType);

export const selectCpNavigatorSlice = createSelector(selectCp, (state) => state.navigator);

export const selectCpNodeSlice = createSelector(selectCp, (state) => state?.nodes);

export const selectCpPage = createSelector(selectCp, (state) => state?.page);

export const selectCpIsPageOpen = createSelector(selectCpPage, (page) => !!page);

export const selectCpModules = createSelector(selectCpPage, (page) => page?.modules ?? []);
export const selectCpModulesAndImaging = createSelector(selectCpPage, (page) => ({
    modules: page?.modules ?? [],
    imaging: page?.imaging,
}));
export const selectCpSelectedNode = createSelector(selectCp, (state) => state.selectedNode);

/**
 * Nodi
 */
export const selectCpAllNodes = createSelector(selectCpNodeSlice, cpNodeSelectors.selectAll);

export const selectCpLevelZeroNodes = createSelector(selectCpNodeSlice, (nodes) =>
    cpNodeSelectors.selectAll(nodes).filter((node) => !node.parentId),
);

export const selectCpNodeFilter = createSelector(selectCpNavigatorSlice, (nav) => nav.filter);

export const selectCpNodeEntities = createSelector(selectCpNodeSlice, cpNodeSelectors.selectEntities);

export const selectCpNode = (id: string) =>
    createSelector(selectCpNodeSlice, (nodes) => cpNodeCustomSelectors.selectOne(nodes, id));

export const selectCpRootNode = createSelector(selectCpNodeSlice, (nodeState) => nodeState.root);

export const selectCpAutoNormalValues = createSelector(selectCpRootNode, (node) => node?.autoNormalValues ?? false);

export const selectNodesForNav = createSelector(selectCpNavigatorSlice, selectCpNodeSlice, (navState, nodeState) => {
    return nodeState.ids
        .filter((id) => {
            const node = nodeState.entities[id];
            if (!node || !node.isVisible) {
                return false;
            }

            if (cpNodeMatchesFilters(navState.filter, node)) {
                return true;
            }

            const childrenIds = cpNodeCustomSelectors.selectTreeChildren(nodeState, node.id);

            return some(childrenIds, (childId) => cpNodeMatchesFilters(navState.filter, nodeState.entities[childId]));
        })
        .map((id) => nodeState.entities[id])
        .filter(isNotUndefined);
});

/**
 * Pagina
 */
export const selectCpCustomPage = createSelector(selectCpPage, (page) => page?.idCustomPage ?? null);

export const selectCpCustomPageRoot = createSelector(selectCpPage, (page) => page?.idCustomPageRoot ?? null);

export const selectCpModelTypes = createSelector(selectCpPage, (page) => page?.idModelTypes ?? null);

export const selectCpIsModified = createSelector(selectCpAllNodes, (nodes) => nodes.some((node) => node.isModified));

export const selectCpNodesIsModified = createSelector(selectCpAllNodes, (nodes) =>
    nodes.filter((node) => node.isModified),
);

export const selectCpNodesIsError = createSelector(selectCpAllNodes, (nodes) => nodes.filter((node) => node.isInError));

export const selectCpNodesIsWarning = createSelector(
    selectCpAllNodes,
    (nodes) => nodes.filter((node) => Boolean(node.warnings.length)));

export const selectCpIsErrorCounter = createSelector(
    selectCpAllNodes,
    (nodes) => nodes.filter((node) => node.isInError).length,
);

export const selectCpIsModifiedCounter = createSelector(
    selectCpAllNodes,
    (nodes) => nodes.filter((node) => node.isModified).length,
);

export const selectCpIsWarningCounter = createSelector(
    selectCpAllNodes,
    (nodes) => nodes.filter((node) => Boolean(node.warnings.length)).length,
);

/*77398 filtrare i warnings per messaggio su out of range*/
export const selectCpHasMeasureOutOfRangeWarningCounter = createSelector(
    selectCpAllNodes,
    (nodes) => nodes.filter((node) => Boolean(node.warnings.length)).length,
);

export const selectCpSources = createSelector(
    selectCpPage,
    (page) => page?.actors ?? [],
)
